/**
 * Deterministic dog name generator for visitor identification
 * Maps visitor hashes to friendly dog names with collision handling
 */

const DOG_NAMES = [
  'Ace', 'Apollo', 'Archie', 'Atlas', 'Bailey', 'Bandit', 'Bear', 'Beau', 'Bella', 'Bentley',
  'Blue', 'Bo', 'Boomer', 'Brady', 'Bruno', 'Brutus', 'Bubba', 'Buck', 'Buddy', 'Buster',
  'Cali', 'Cash', 'Chance', 'Charlie', 'Chase', 'Chester', 'Chloe', 'Cleo', 'Coco', 'Cody',
  'Cookie', 'Cooper', 'Daisy', 'Dakota', 'Dante', 'Dexter', 'Diesel', 'Dixie', 'Duke', 'Echo',
  'Eddie', 'Ella', 'Ellie', 'Emma', 'Finn', 'Frankie', 'George', 'Ginger', 'Gizmo', 'Grace',
  'Gracie', 'Gunner', 'Gus', 'Hazel', 'Hank', 'Harley', 'Henry', 'Hershey', 'Holly', 'Honey',
  'Hunter', 'Ivy', 'Jack', 'Jackson', 'Jake', 'Jasper', 'Jax', 'Joey', 'Koda', 'Kong',
  'Layla', 'Leo', 'Lexi', 'Lilly', 'Lola', 'Louie', 'Lucy', 'Luke', 'Luna', 'Macy',
  'Maddie', 'Maggie', 'Marley', 'Max', 'Maya', 'Mia', 'Milo', 'Millie', 'Minnie', 'Mocha',
  'Molly', 'Murphy', 'Nala', 'Nash', 'Nelson', 'Nico', 'Nova', 'Oakley', 'Oliver', 'Ollie',
  'Oreo', 'Oscar', 'Otis', 'Ozzy', 'Peanut', 'Penny', 'Pepper', 'Phoebe', 'Piper', 'Poppy',
  'Prince', 'Princess', 'Ranger', 'Remi', 'Rex', 'Riley', 'Rocco', 'Rocky', 'Romeo', 'Roscoe',
  'Rose', 'Rosie', 'Roxie', 'Ruby', 'Rudy', 'Rusty', 'Sadie', 'Sam', 'Sammy', 'Sandy',
  'Scout', 'Shadow', 'Sheba', 'Shelby', 'Simba', 'Sophie', 'Sparky', 'Stella', 'Storm', 'Sugar',
  'Sunny', 'Tank', 'Teddy', 'Thor', 'Tilly', 'Titan', 'Toby', 'Tucker', 'Tyson', 'Vader',
  'Vinnie', 'Violet', 'Walter', 'Watson', 'Whiskey', 'Willow', 'Winston', 'Xena', 'Yoda', 'Zeus',
  'Ziggy', 'Zoey', 'Zoe', 'Banjo', 'Biscuit', 'Blaze', 'Bolt', 'Bones', 'Boots', 'Brownie',
  'Captain', 'Chief', 'Chip', 'Clifford', 'Copper', 'Cricket', 'Dash', 'Doc', 'Felix', 'Flash',
  'Ghost', 'Goose', 'Groot', 'Hobbes', 'Jazz', 'King', 'Lucky', 'Maverick', 'Moose', 'Ninja',
  'Patches', 'Paws', 'Pickles', 'Pixel', 'Pluto', 'Rascal', 'Rebel', 'Rocket', 'Sarge', 'Scooby',
]

/**
 * Simple hash function (djb2 algorithm)
 * Returns a positive integer hash for any string
 */
function hashString(str: string): number {
  let hash = 5381
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i)
  }
  return Math.abs(hash)
}

/**
 * Get a deterministic dog name for a single visitor hash
 * Note: For batch processing with collision handling, use createDogNameMap
 */
export function getDogName(visitorHash: string): string {
  const hash = hashString(visitorHash)
  const nameIndex = hash % DOG_NAMES.length
  return DOG_NAMES[nameIndex]
}

/**
 * Create a mapping of visitor hashes to unique dog names
 * Handles collisions by adding numeric suffixes (e.g., "Buddy-2")
 *
 * @param visitorHashes - Array of visitor hashes to map
 * @returns Map from visitor hash to unique dog name
 */
export function createDogNameMap(visitorHashes: string[]): Map<string, string> {
  const nameMap = new Map<string, string>()
  const nameUsage = new Map<string, number>() // baseName -> count of times used

  // Sort hashes to ensure deterministic ordering across calls
  const sortedHashes = [...new Set(visitorHashes)].sort()

  for (const hash of sortedHashes) {
    const baseName = getDogName(hash)
    const currentCount = nameUsage.get(baseName) || 0

    // First occurrence gets plain name, subsequent get suffix
    const finalName = currentCount === 0 ? baseName : `${baseName}-${currentCount + 1}`

    nameMap.set(hash, finalName)
    nameUsage.set(baseName, currentCount + 1)
  }

  return nameMap
}

/**
 * Hook-friendly version that returns a lookup function
 * Useful for components that receive visitor data incrementally
 */
export function createDogNameLookup(visitorHashes: string[]): (hash: string) => string {
  const nameMap = createDogNameMap(visitorHashes)
  return (hash: string) => nameMap.get(hash) || getDogName(hash)
}
